import {Fragment} from "react";
import {Field, Select} from "@bolteu/clay-react";
import {Controller, type Control, type FieldValues} from "react-hook-form";

import {useI18n} from "i18n/i18n";

import {FieldLabel} from "../FieldLabel";

import * as Styled from "./dropdown.styled";
import {type GroupedItems, type Item} from "./types";

interface OwnProps {
    label?: string;
    items?: Item[];
    groupedItems?: Array<GroupedItems<Item>>;
    placeholder: string;
    required?: boolean;
    fieldName: string;
    control: Control<FieldValues> | undefined;
    noTopSpacing?: boolean;
}

// Form dropdown on Clay Select (replaces the monolith's custom bottom-sheet Dropdown).
// The field value is the selected item's id; Select maps id -> label for the trigger.
export const DropdownController = ({
    label,
    items,
    groupedItems,
    placeholder,
    required,
    control,
    fieldName,
    noTopSpacing,
}: OwnProps) => {
    const i18n = useI18n();

    const flatItems = items ?? groupedItems?.flatMap((group) => group.items) ?? [];
    const selectItems = flatItems.map((item) => ({value: item.id, label: item.title}));

    const renderItem = (item: Item) => (
        <Select.Item key={item.id} value={item.id}>
            <Select.ItemText>{item.title}</Select.ItemText>
            <Select.ItemIndicator />
        </Select.Item>
    );

    return (
        <Controller
            rules={{required: required && i18n("webform.form_field.required_field")}}
            control={control}
            name={fieldName}
            render={({field: {value, onChange}, fieldState: {error}}) => (
                <Field.Root
                    className={noTopSpacing ? undefined : "mt-6"}
                    invalid={!!error?.message}
                    required={required}
                >
                    {!!label && <FieldLabel label={label} />}
                    <Select.Root
                        items={selectItems}
                        value={value?.id ?? null}
                        onValueChange={(id) => onChange(flatItems.find((item) => item.id === id) ?? null)}
                        size="md"
                        multiline
                        required={required}
                    >
                        <Select.Trigger>
                            <Select.Value placeholder={placeholder} />
                            <Select.Icon />
                        </Select.Trigger>
                        <Select.Portal>
                            <Select.Positioner>
                                <Select.Popup>
                                    <Select.List>
                                        {items
                                            ? items.map(renderItem)
                                            : groupedItems?.map((group) => (
                                                  <Fragment key={group.id}>
                                                      <Styled.GroupTitle>{group.title}</Styled.GroupTitle>
                                                      {group.items.map(renderItem)}
                                                  </Fragment>
                                              ))}
                                    </Select.List>
                                </Select.Popup>
                            </Select.Positioner>
                        </Select.Portal>
                    </Select.Root>
                    <Field.Error match={!!error?.message}>{error?.message}</Field.Error>
                </Field.Root>
            )}
        />
    );
};
